/**
 * KioskGeraetSetup — Kopplung eines Stempel-Terminals (#1809-Fix).
 *
 * Ein Admin legt das Terminal in den Einstellungen an und erhaelt einen
 * 8-stelligen Kopplungscode. Hier am Tablet wird er eingegeben; das Geraet
 * bekommt dafuer seinen Device-Token und gilt ab dem naechsten Seitenaufruf
 * als Kiosk (hart hell, kein Dark-Mode-Schalter, Button statt Slider).
 *
 * Die Seite ist bewusst ohne Login erreichbar: auf work.weship.eu gibt es
 * keinen E-Mail/Passwort-Login, ein Admin koennte sich am Tablet gar nicht
 * anmelden. Geschuetzt ist die Ausgabe des Codes, nicht seine Einloesung.
 *
 * @page /geraet-einrichten (unauthentifiziert)
 * @roles keine (Code-Auth)
 * @endpoints GET /api/kiosk-geraete/status, POST /api/kiosk-geraete/koppeln
 * @since v2.867.0
 */
import { useState, useEffect, useCallback } from 'react';
import { MonitorSmartphone, Check, AlertCircle } from 'lucide-react';
import { geraetHeaders, koppleGeraet, clearGeraetToken } from '../utils/kioskDevice';

const API = import.meta.env.VITE_API_URL || '';

export default function KioskGeraetSetup() {
  const [status, setStatus] = useState(null); // { kiosk, name }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const ladeStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kiosk-geraete/status`, { headers: { ...geraetHeaders() } });
      setStatus(await res.json());
    } catch {
      setStatus({ kiosk: false });
    }
  }, []);

  useEffect(() => { ladeStatus(); }, [ladeStatus]);

  const koppeln = async () => {
    setError('');
    setSaving(true);
    try {
      // #1820 — Einloesung liegt in utils/kioskDevice, damit dieser Weg und der
      // Einrichtungsschirm im Terminal dieselbe Logik benutzen.
      const ergebnis = await koppleGeraet(code);
      if (!ergebnis.ok) { setError(ergebnis.error); return; }
      setCode('');
      // #1816 AK3 — direkt auf die Scan-Seite, nicht auf /portal. Wer ein
      // Terminal koppelt, will es danach benutzen; ein Zwischenschritt ist ein
      // zusaetzlicher Gang zum Tablet. Bewusst harte Navigation statt Router:
      // der Kiosk soll frisch starten (Vollbild, Scanner-Listener, Theme).
      // Nur hier, nicht im Terminal: diese Seite wird am Desktop bedient, wo
      // ein Adresswechsel unproblematisch ist (#1820).
      window.location.href = '/kiosk';
    } finally {
      setSaving(false);
    }
  };

  const entkoppeln = async () => {
    clearGeraetToken();
    await ladeStatus();
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--bg-page,#F8F9FC)] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-[420px]">
        <div className="flex flex-col items-center mb-6">
          <MonitorSmartphone className="w-10 h-10 text-[#338edc] mb-3" />
          <h1 className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)] text-center">
            Terminal einrichten
          </h1>
        </div>

        {status?.kiosk ? (
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-600 shrink-0" />
              <p className="text-lg text-[var(--text-primary,#1A1A2E)]">
                Dieses Gerät ist als Terminal registriert.
              </p>
            </div>
            <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-xl px-4 py-3">
              <div className="text-xs text-[var(--text-tertiary,#6B7280)]">Name</div>
              <div className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">{status.name}</div>
              {status.standort && (
                <div className="text-sm text-[var(--text-secondary,#4B5563)] mt-1">{status.standort}</div>
              )}
            </div>
            <p className="text-sm text-[var(--text-secondary,#4B5563)]">
              Das Terminal ist ohne PIN scanbereit. Eine laufende Anmeldung bleibt
              bestehen — es muss sich niemand neu anmelden.
            </p>
            <a
              href="/kiosk"
              className="block w-full text-center bg-[#338edc] hover:bg-[#2b7bc4] text-white font-bold text-lg rounded-2xl leading-[56px]"
              style={{ height: 56 }}
            >
              Zur Scan-Seite
            </a>
            <button
              type="button"
              onClick={entkoppeln}
              className="w-full text-center text-sm text-[var(--text-tertiary,#6B7280)] font-medium py-2"
            >
              Registrierung von diesem Gerät entfernen
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl p-6 space-y-4">
              <p className="text-sm text-[var(--text-secondary,#4B5563)]">
                Kopplungscode aus <strong>Einstellungen → Stempeluhr → Terminals</strong> eingeben.
                Der Code ist 30 Minuten gültig und nur einmal verwendbar.
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 8)); setError(''); }}
                className="w-full bg-[var(--bg-input,#F8F9FC)] border border-[var(--border-input,#E5E7EB)] rounded-xl px-4 text-3xl text-center font-mono font-bold tracking-[0.2em] text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20"
                style={{ height: 64 }}
                placeholder="00000000"
                autoComplete="off"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 justify-center text-red-500">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <p className="text-base font-semibold">{error}</p>
              </div>
            )}

            <button
              type="button"
              onClick={koppeln}
              disabled={code.length !== 8 || saving}
              className="w-full bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/30 disabled:cursor-not-allowed text-white font-bold text-lg rounded-2xl transition-colors"
              style={{ height: 56 }}
            >
              {saving ? 'Wird gekoppelt…' : 'Gerät koppeln'}
            </button>

            <p className="text-center text-sm text-[var(--text-tertiary,#6B7280)]">
              Ohne Kopplung funktioniert das Gerät ganz normal weiter — es zeigt dann
              nur die persönliche Ansicht statt der Terminal-Ansicht.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
